// Frontend: services/api.js
import axios from 'axios';
import { cacheManager } from './utils/cacheManager';
import { flashMessage } from './utils/flashMessage';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: (process.env.REACT_APP_API_BASE_URL || '/backend/php') + '/api',
  timeout: 30000  // Increase to 30s to avoid transient backend timeouts
});

// Simple retry defaults for idempotent requests
api.defaults.retry = 2; // number of retries for GET/network errors
api.defaults.retryDelay = 1000; // base delay in ms for retries

// Request interceptor to add JWT token and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      // Add Bearer prefix to token
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Sending request with token:', token.substring(0, 20) + '...');
    } else {
      console.log('No token found in localStorage');
    }
    
    // If sending FormData, let the browser set Content-Type header automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
      console.log('Sending FormData with multipart/form-data');
      // Increase timeout for uploads (default 30s may be too low for large files)
      config.timeout = config.timeout || 300000; // 5 minutes for large uploads
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token expiration, cache responses, and add retry logic
api.interceptors.response.use(
  (response) => {
    // Cache successful GET responses
    if (response.status === 200 && (response.config.method === 'get' || response.config.method === 'GET')) {
      cacheManager.set(response.config.url, response.data);
      console.log(`[Cache SET] ${response.config.url}`);
    }
    return response;
  },
  async (error) => {
    const config = error.config || {};

    // Try to return cached data if network fails on GET requests
    if ((config.method === 'get' || config.method === 'GET') && !error.response) {
      const cached = cacheManager.get(config.url);
      if (cached) {
        console.log(`[Cache HIT - Fallback] ${config.url}`);
        return Promise.resolve({
          data: cached,
          status: 200,
          statusText: 'OK (from cache)',
          config,
          headers: {}
        });
      }
    }

    // If unauthorized, clear token and redirect to login with flash message
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      flashMessage.set('Session Timeout: Your session has expired. Please log in again.', 'warning');
      window.location.href = '/admin/login';
      return Promise.reject(error);
    }

    // 4xx client errors (except 429) shouldn't be retried
    if (error.response?.status >= 400 && error.response?.status < 500 && error.response?.status !== 429) {
      return Promise.reject(error);
    }

    // Decide if this is a retryable failure (timeout, network error, or server error)
    const isTimeout = error.code === 'ECONNABORTED' || (error.message || '').toLowerCase().includes('timeout');
    const isNetworkError = !error.response;
    const isServerError = error.response?.status >= 500;

    const shouldRetry = (isTimeout || isNetworkError || isServerError) && (config.method || '').toLowerCase() === 'get';

    if (shouldRetry) {
      config.__retryCount = config.__retryCount || 0;
      const maxRetries = config.retry || api.defaults.retry || 0;
      const retryDelay = config.retryDelay || api.defaults.retryDelay || 1000;

      if (config.__retryCount < maxRetries) {
        config.__retryCount += 1;
        const delay = config.__retryCount * retryDelay;
        console.warn(`Request failed (${error.message}). Retrying attempt ${config.__retryCount}/${maxRetries} after ${delay}ms`);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return api(config);
      }
    }

    // No more retries: surface the error
    return Promise.reject(error);
  }
);

// Project API methods
export const projectAPI = {
  // Get all projects
  getProjects: () => api.get('/projects'),
  
  // Get single project
  getProject: (id) => api.get(`/projects/${id}`),
  
  // Create project
  createProject: (projectData) => api.post('/projects', projectData),
  
  // Update project
  updateProject: (id, projectData) => api.put(`/projects/${id}`, projectData),
  
  // Delete project (requires superadmin)
  deleteProject: async (id) => {
    try {
      console.log(`Attempting to delete project ${id}`);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Decode token to check role (optional verification)
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      console.log('Token payload:', tokenPayload);
      console.log('User role:', tokenPayload.role);
      
      if (tokenPayload.role !== 'superadmin') {
        throw new Error('Insufficient permissions. Superadmin role required.');
      }
      
      const response = await api.delete(`/projects/${id}`);
      console.log('Delete successful:', response.data);
      return response;
    } catch (error) {
      console.error('Delete project error:', error);
      throw error;
    }
  }
};

// Event API methods
export const eventAPI = {
  // Get all events
  getEvents: () => api.get('/events'),
  
  // Get single event
  getEvent: (id) => api.get(`/events/${id}`),
  
  // Create event
  createEvent: (eventData, isFormData = false) => api.post('/events', eventData),
  
  // Update event
  updateEvent: (id, eventData, isFormData = false) => api.put(`/events/${id}`, eventData),
  
  // Delete event (requires superadmin)
  deleteEvent: async (id) => {
    try {
      console.log(`Attempting to delete event ${id}`);
      const token = localStorage.getItem('token');
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Decode token to check role (optional verification)
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      console.log('Token payload:', tokenPayload);
      console.log('User role:', tokenPayload.role);
      
      if (tokenPayload.role !== 'superadmin') {
        throw new Error('Insufficient permissions. Superadmin role required.');
      }
      
      const response = await api.delete(`/events/${id}`);
      console.log('Delete successful:', response.data);
      return response;
    } catch (error) {
      console.error('Delete event error:', error);
      throw error;
    }
  }
};

export default api;